import './CloudPlatforms.css';
import Hero from '../../components/Hero/Hero';
import Providers from '../../components/Providers/Providers';
import ServiceSection from '../../components/ServiceSection/ServiceSection';
import Architecture from '../../components/Architecture/Architecture';
import Stats from '../../components/Stats/Stats';
import Timeline from '../../components/Timeline/Timeline';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

const services = [
  {
    title: 'Cloud Migration',
    description:
      'Move applications, containers, and data to hosted environments with minimal downtime, secure staging, and automated rollbacks.',
    features: [
      'Cloud readiness assessment and migration plan',
      'Automated lift-and-shift and refactor pipelines',
      'Validation, monitoring, and rollback controls',
    ],
    image: '/images/cloud/migration.webp',
  },
  {
    title: 'Cloud Native Development',
    description:
      'Build resilient microservices, serverless APIs, and Kubernetes-native applications that scale with modern architecture.',
    features: [
      'Cloud-first architecture design',
      'API-driven microservices and serverless APIs',
      'Production-grade observability and deployment',
    ],
    image: '/images/cloud/cloud-native.webp',
  },
  {
    title: 'DevOps Automation',
    description:
      'Automate infrastructure, CI/CD delivery, and release management so deployments are repeatable, secure, and fast.',
    features: [
      'GitOps pipelines with CI/CD workflows',
      'Infrastructure as Code and environment drift controls',
      'Testing automation, secrets hygiene, and release gates',
    ],
    image: '/images/cloud/devops.webp',
  },
  {
    title: 'Cloud Security',
    description:
      'Protect your cloud estate with identity, encryption, zero-trust networking, and threat detection across platforms.',
    features: [
      'Identity and access governance',
      'Encrypted networks, storage, and secrets',
      'Continuous posture monitoring and audit readiness',
    ],
    image: '/images/cloud/security.webp',
  },
  {
    title: 'Monitoring & Observability',
    description:
      'Gain full visibility into applications and infrastructure with centralized logs, metrics, and alerting.',
    features: [
      'Tracing, logs, and metrics in one pane',
      'Automated anomaly detection and alerting',
      'Performance tuning for cloud-scale workloads',
    ],
    image: '/images/cloud/monitoring.webp',
  },
  {
    title: 'Cost Optimization',
    description:
      'Control cloud spend while improving performance through rightsizing, autoscaling, and FinOps discipline.',
    features: [
      'Reserved capacity and burst controls',
      'Lifecycle policies and spend reporting',
      'Cost governance for multi-cloud portfolios',
    ],
    image: '/images/cloud/cost.webp',
  },
];

const whyCards = [
  { title: '99.99% Availability', subtitle: 'High-availability designs with active redundancy.' },
  { title: '24×7 Monitoring', subtitle: 'Round-the-clock observability and incident response.' },
  { title: 'Certified Engineers', subtitle: 'Cloud architects with AWS, Azure, and GCP certifications.' },
  { title: 'Enterprise Security', subtitle: 'Zero-trust controls, encryption, and compliance-ready frameworks.' },
  { title: 'Multi Cloud', subtitle: 'Strategy, deployment, and governance across all major clouds.' },
  { title: 'DevOps Experts', subtitle: 'Release velocity with stability, reliability, and automation.' },
];

const architectureSteps = [
  'Client',
  'CDN',
  'Load Balancer',
  'Kubernetes',
  'Microservices',
  'Redis',
  'Database',
  'Object Storage',
];

const faqItems = [
  {
    question: 'How long does a cloud migration project take?',
    answer:
      'Typical migrations range from 6 to 14 weeks depending on complexity, application dependencies, and compliance requirements.',
  },
  {
    question: 'Which cloud platforms does VanByte support?',
    answer: 'We build and manage solutions across AWS, Azure, and Google Cloud Platform with a focus on hybrid and multi-cloud architectures.',
  },
  {
    question: 'Can you improve an existing cloud deployment?',
    answer:
      'Yes. We perform platform audits, optimize cloud spend, implement security hardening, and modernize legacy cloud configurations.',
  },
  {
    question: 'Do you provide ongoing managed cloud services?',
    answer:
      'Our managed services include 24/7 monitoring, incident response, cost governance, and continuous improvement for cloud operations.',
  },
];

const galleryImages = [
  { title: 'AWS', image: '/images/cloud/aws.webp' },
  { title: 'Azure', image: '/images/cloud/azure.webp' },
  { title: 'GCP', image: '/images/cloud/gcp.webp' },
  { title: 'Hero Cloud', image: '/images/cloud/hero-cloud.webp' },
  { title: 'Cloud Platform', image: '/images/cloud/cloud_platform.png' },
  { title: 'Cloud Service', image: '/images/cloud/cloud_cloud_service.png' },
  { title: 'Cloud Built for Future', image: '/images/cloud/cloud_built_for_future.png' },
  { title: 'Scalable Elastic', image: '/images/cloud/cloud_scalable_elastic.png' },
  { title: 'Secure Reliable', image: '/images/cloud/cloud_secure_reliable.png' },
  { title: 'Migration', image: '/images/cloud/migration.webp' },
  { title: 'Cloud Native', image: '/images/cloud/cloud-native.webp' },
  { title: 'DevOps', image: '/images/cloud/devops.webp' },
  { title: 'Security', image: '/images/cloud/security.webp' },
  { title: 'Monitoring', image: '/images/cloud/monitoring.webp' },
  { title: 'Cost', image: '/images/cloud/cost.webp' },
];

export default function CloudPlatforms() {
  return (
    <article className="cloud-page">
      <Hero />

      <section className="section section-supported" aria-labelledby="trusted-clouds-heading">
        <div className="section-heading">
          <span className="eyebrow">Trusted Clouds</span>
          <h2 id="trusted-clouds-heading">We architect premium experiences on the top cloud platforms.</h2>
          <p>From enterprise migrations to cloud-native transformation, we deliver secure, scalable systems built for growth.</p>
        </div>
        <Providers />
      </section>

      <section className="section section-gallery" aria-labelledby="gallery-heading">
        <div className="section-heading">
          <span className="eyebrow">Cloud Asset Library</span>
          <h2 id="gallery-heading">Mapped visuals from our cloud asset set.</h2>
          <p>Every published cloud image from the brand folder is displayed as a connected visual reference.</p>
        </div>
        <div className="gallery-grid">
          {galleryImages.map((item) => (
            <article key={item.title} className="gallery-card">
              <img src={item.image} alt={item.title} />
              <p>{item.title}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section section-services" id="services" aria-labelledby="services-heading">
        <div className="section-heading">
          <span className="eyebrow">Core Services</span>
          <h2 id="services-heading">Services built to modernize cloud operations.</h2>
        </div>
        <div className="service-stack">
          {services.map((service, index) => (
            <ServiceSection
              key={service.title}
              title={service.title}
              description={service.description}
              features={service.features}
              image={service.image}
              reverse={index % 2 !== 0}
            />
          ))}
        </div>
      </section>

      <section className="section section-architecture" aria-labelledby="architecture-heading">
        <div className="section-heading">
          <span className="eyebrow">Cloud Architecture</span>
          <h2 id="architecture-heading">A polished infrastructure path for modern applications.</h2>
          <p>Visualize how client traffic moves through CDN, load balancing, Kubernetes, services, and resilient storage.</p>
        </div>
        <Architecture steps={architectureSteps} />
      </section>

      <section className="section section-why" aria-labelledby="why-heading">
        <div className="section-heading section-heading--split">
          <span className="eyebrow">Why VanByte</span>
          <div>
            <h2 id="why-heading">Premium cloud delivery with trusted engineering and governance.</h2>
            <p>Our cloud strategy blends performance, security, and operational rigor across every deployment.</p>
          </div>
        </div>
        <div className="why-grid">
          {whyCards.map((card) => (
            <article key={card.title} className="why-card">
              <h3>{card.title}</h3>
              <p>{card.subtitle}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section section-metrics" aria-labelledby="metrics-heading">
        <div className="section-heading">
          <span className="eyebrow">How we measure success</span>
          <h2 id="metrics-heading">Cloud outcomes that move the business forward.</h2>
        </div>
        <Stats />
      </section>

      <section className="section section-process" aria-labelledby="process-heading">
        <div className="section-heading">
          <span className="eyebrow">Process</span>
          <h2 id="process-heading">A clear path from assessment to continuous optimization.</h2>
        </div>
        <Timeline />
      </section>

      <section className="section section-faq" aria-labelledby="faq-heading">
        <div className="section-heading">
          <span className="eyebrow">Frequently asked questions</span>
          <h2 id="faq-heading">Precise answers for cloud teams and leadership.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection />
      <Footer />
    </article>
  );
}
